import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import {
  Loader2,
  Settings as SettingsIcon,
  Globe,
  Building2,
  Save,
  CalendarDays,
  Plus,
  Trash2,
  Pencil,
  X,
  Bot,
  AlertCircle,
  PauseCircle,
  PlayCircle,
  MessageSquare,
  CreditCard,
} from 'lucide-react';
import { WhatsAppDashboard } from './WhatsAppDashboard';
import { CenterFaqsSection } from '../components/settings/CenterFaqsSection';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';

// ─── Day helpers ────────────────────────────────────────────────────────────

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const emptyDay = () => ({ slots: [], breaks: [] });
const emptyDays = () => Object.fromEntries(DAY_KEYS.map(k => [k, emptyDay()]));
const emptyTemplate = () => ({ name: '', days: emptyDays() });

export const faqStatusFor = (faqs) => faqs.length ? 'SUCCESS_NONEMPTY' : 'SUCCESS_EMPTY';

// Ensure all 7 day keys are present (backend may return sparse objects)
const normalizeDays = (days) => {
  const base = emptyDays();
  for (const k of DAY_KEYS) {
    if (days?.[k]) {
      base[k] = {
        slots: days[k].slots || [],
        breaks: days[k].breaks || [],
      };
    }
  }
  return base;
};

// ─── Component ──────────────────────────────────────────────────────────────

export const Settings = () => {
  const { api, user, language, setLanguage, isSuperAdmin, isCenterAdmin } = useAuth();
  const { t } = useTranslation();

  // ── Center settings state ──────────────────────────────────────
  const [center, setCenter] = useState(null);
  const [loadingCenter, setLoadingCenter] = useState(true);
  const [saving, setSaving] = useState(false);
  const [centerForm, setCenterForm] = useState({});
  const [slugError, setSlugError] = useState('');
  const [savingCalendarId, setSavingCalendarId] = useState(null);

  // ── Billing state (T-65) ────────────────────────────────────────
  const [loadingPaymentLink, setLoadingPaymentLink] = useState(false);
  const [billingPaymentLinkUnknown, setBillingPaymentLinkUnknown] = useState(false);
  const billingRequestRef = useRef(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [loadingConnect, setLoadingConnect] = useState(false);

  // ── Weekly templates state ─────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const templatesRequestRef = useRef(null);
  const templatesCleanupTimerRef = useRef(null);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // null = create
  const [templateForm, setTemplateForm] = useState(emptyTemplate());
  const [activeDay, setActiveDay] = useState('mon');
  const [clientSearch, setClientSearch] = useState({});  // keyed by slot index
  const [savingTemplate, setSavingTemplate] = useState(false);

  // ── AI Secretary state ─────────────────────────────────────────
  const [aiSecConfig, setAiSecConfig] = useState(null);
  const [aiSecForm, setAiSecForm] = useState({
    businessName: '',
    businessInfo: '',
    welcomeMessage: '',
    fallbackMessage: '',
    cancellationPolicyText: '',
    humanHandoffEnabled: true,
  });
  const [savingAiSec, setSavingAiSec] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);
  const [editingFaqIndex, setEditingFaqIndex] = useState(null);
  const [faqDraft, setFaqDraft] = useState({ question: '', answer: '' });
  const [faqStatus, setFaqStatus] = useState('LOADING');
  const [faqError, setFaqError] = useState('');
  const [savingFaqs, setSavingFaqs] = useState(false);
  const [faqSaveError, setFaqSaveError] = useState('');
  const [faqSaveSuccess, setFaqSaveSuccess] = useState(false);

  const fetchAiSecConfig = useCallback(async () => {
    if (!isCenterAdmin) return;
    try {
      const res = await api.get('/ai-secretary/config');
      setAiSecConfig(res.data);
      setAiSecForm({
        businessName: res.data.businessName || '',
        businessInfo: res.data.businessInfo || '',
        welcomeMessage: res.data.welcomeMessage || '',
        fallbackMessage: res.data.fallbackMessage || '',
        cancellationPolicyText: res.data.cancellationPolicyText || '',
        humanHandoffEnabled: res.data.humanHandoffEnabled ?? true,
      });
    } catch {
      // ignore if not provisioned yet
    }
  }, [api, isCenterAdmin]);

  useEffect(() => { fetchAiSecConfig(); }, [fetchAiSecConfig]);

  const handleTogglePause = async () => {
    if (!aiSecConfig) return;
    setTogglingPause(true);
    try {
      const res = await api.put('/ai-secretary/pause', { paused: aiSecConfig.enabled });
      setAiSecConfig(res.data);
      toast.success(aiSecConfig.enabled ? 'Secretaria IA pausada' : 'Secretaria IA reactivada');
    } catch {
      toast.error(t('success.settings.aiSecretaryToggleError'));
    } finally {
      setTogglingPause(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    if (loadingPaymentLink) return;
    const controller = new AbortController();
    const request = { controller, timedOut: false };
    billingRequestRef.current = request;
    const timeoutId = setTimeout(() => {
      request.timedOut = true;
      controller.abort();
    }, 20000);
    setLoadingPaymentLink(true);
    setBillingPaymentLinkUnknown(false);
    try {
      const res = await api.post('/billing/update-payment-method-link', undefined, { signal: controller.signal });
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      if (billingRequestRef.current !== request) return;
      if (request.timedOut || !err?.response || err?.code === 'ERR_CANCELED') {
        setBillingPaymentLinkUnknown(true);
      } else if (err?.response?.status === 429) {
        toast.error(t('errors.rate_limit_exceeded'));
      } else {
        toast.error(err?.response?.data?.detail || 'No se pudo generar el enlace de actualización de tarjeta');
      }
    } finally {
      clearTimeout(timeoutId);
      if (billingRequestRef.current === request) {
        billingRequestRef.current = null;
        setLoadingPaymentLink(false);
      }
    }
  };

  const handleToggleAutoAddon = async () => {
    if (!aiSecConfig) return;
    const next = !(aiSecConfig.autoAddonEnabled ?? true);
    try {
      const res = await api.put('/ai-secretary/config', { autoAddonEnabled: next });
      setAiSecConfig(res.data);
      toast.success(next ? 'Ampliaciones automáticas activadas' : 'Ampliaciones automáticas desactivadas');
    } catch {
      toast.error(t('success.settings.configUpdateError'));
    }
  };

  const handleSaveAiSec = async () => {
    setSavingAiSec(true);
    try {
      const res = await api.put('/ai-secretary/config', aiSecForm);
      setAiSecConfig(res.data);
      setAiSecForm({
        businessName: res.data.businessName || '',
        businessInfo: res.data.businessInfo || '',
        welcomeMessage: res.data.welcomeMessage || '',
        fallbackMessage: res.data.fallbackMessage || '',
        cancellationPolicyText: res.data.cancellationPolicyText || '',
        humanHandoffEnabled: res.data.humanHandoffEnabled ?? true,
      });
      setEditingFaqIndex(null);
      setFaqDraft({ question: '', answer: '' });
      toast.success(t('success.centers.configSaved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingAiSec(false);
    }
  };

  const handleFaqSave = () => {
    if (!faqDraft.question.trim() || !faqDraft.answer.trim()) return;
    const updated = [...(centerForm.faqs || [])];
    if (editingFaqIndex !== null) {
      updated[editingFaqIndex] = { ...faqDraft };
    } else {
      updated.push({ ...faqDraft });
    }
    setCenterForm(f => ({ ...f, faqs: updated }));
    setEditingFaqIndex(null);
    setFaqDraft({ question: '', answer: '' });
  };

  const handleFaqDelete = (idx) => {
    setCenterForm(f => ({ ...f, faqs: (f.faqs || []).filter((_, i) => i !== idx) }));
  };

  const handleFaqEdit = (idx) => {
    setEditingFaqIndex(idx);
    setFaqDraft({ ...(centerForm.faqs || [])[idx] });
  };

  const handleSaveFaqs = async () => {
    setSavingFaqs(true);
    setFaqSaveError('');
    setFaqSaveSuccess(false);
    const faqs = (centerForm.faqs || []).map(({ question, answer }) => ({
      question: question.trim(),
      answer: answer.trim(),
    }));
    try {
      await api.put(`/centers/${center.id}/faqs`, { faqs });
      await fetchCenter();
      setFaqSaveSuccess(true);
      setEditingFaqIndex(null);
      setFaqDraft({ question: '', answer: '' });
    } catch (err) {
      setFaqSaveError(err?.response?.data?.detail || 'Error al guardar las preguntas frecuentes');
    } finally {
      setSavingFaqs(false);
    }
  };

  // ── Fetch center ───────────────────────────────────────────────
  const fetchCenter = useCallback(async () => {
    setFaqStatus('LOADING');
    setFaqError('');
    try {
      setLoadingCenter(true);
      if (user.centerId) {
        const res = await api.get(`/centers/${user.centerId}`);
        setCenter(res.data);
        const faqs = Array.isArray(res.data.faqs) ? res.data.faqs : [];
        setCenterForm({ ...res.data, faqs, allowOverlap: res.data.allowOverlap ?? false, staffPlannerVisibility: res.data.staffPlannerVisibility ?? 'ALL', clientServiceVisibility: res.data.clientServiceVisibility ?? 'ALL' });
        setFaqStatus(faqStatusFor(faqs));
      }
    } catch (err) {
      setFaqStatus('ERROR+RETRY');
      setFaqError(err?.response?.data?.detail || 'No se pudieron cargar las preguntas frecuentes');
    } finally {
      setLoadingCenter(false);
    }
  }, [api, user]);

  useEffect(() => { fetchCenter(); }, [fetchCenter]);

  const fetchConnectStatus = useCallback(async () => {
    if (!isCenterAdmin) return;
    try {
      const res = await api.get('/stripe/connect/status');
      setConnectStatus(res.data);
    } catch {
      setConnectStatus(null);
    }
  }, [api, isCenterAdmin]);

  useEffect(() => { fetchConnectStatus(); }, [fetchConnectStatus]);

  const handleConnectAction = async () => {
    if (loadingConnect) return;
    setLoadingConnect(true);
    try {
      let current = connectStatus;
      if (!current?.enabled) {
        const enabled = await api.post('/stripe/connect/enable');
        current = { ...current, ...enabled.data, enabled: true };
        setConnectStatus(current);
      }
      const link = await api.post('/stripe/connect/onboarding-link');
      window.location.assign(link.data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('settings.connect.error'));
    } finally {
      setLoadingConnect(false);
    }
  };

  // ── Fetch templates + supporting data ─────────────────────────
  const fetchTemplates = useCallback(async () => {
    if (!isCenterAdmin) return;
    const pendingCleanup = templatesRequestRef.current;
    if (pendingCleanup?.cleanupPending) {
      clearTimeout(templatesCleanupTimerRef.current);
      templatesCleanupTimerRef.current = null;
      pendingCleanup.cleanupPending = false;
      return;
    }
    if (templatesRequestRef.current && !templatesRequestRef.current.controller.signal.aborted) return;
    templatesRequestRef.current?.controller.abort();
    const controller = new AbortController();
    const request = { controller, timedOut: false, cleanupPending: false };
    templatesRequestRef.current = request;
    setLoadingTemplates(true);
    setTemplatesError('');
    const timeoutId = setTimeout(() => {
      request.timedOut = true;
      controller.abort();
    }, 20000);
    try {
      const requestConfig = { signal: controller.signal };
      const [tmplRes, svcRes, cliRes, staffRes] = await Promise.all([
        api.get('/weekly-templates', requestConfig),
        api.get('/calendars', requestConfig),
        api.get('/clients', requestConfig),
        api.get('/users?role=STAFF', requestConfig),
      ]);
      if (templatesRequestRef.current !== request) return;
      setTemplates(tmplRes.data);
      setServices(svcRes.data.filter(s => s.active));
      setClients(cliRes.data.filter(c => c.active));
      setStaffUsers(staffRes.data.filter(s => s.active !== false));
    } catch (err) {
      if (templatesRequestRef.current !== request || !request.timedOut && (err?.code === 'ERR_CANCELED' || !err?.response)) return;
      setTemplatesError(t('settings.toast.loadTemplatesError'));
    } finally {
      clearTimeout(timeoutId);
      if (templatesRequestRef.current === request) {
        templatesRequestRef.current = null;
        setLoadingTemplates(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, isCenterAdmin]);

  useEffect(() => {
    fetchTemplates();
    return () => {
      const request = templatesRequestRef.current;
      if (!request) return;
      request.cleanupPending = true;
      queueMicrotask(() => {
        if (templatesRequestRef.current === request && request.cleanupPending) {
          templatesRequestRef.current = null;
          request.controller.abort();
        }
        templatesCleanupTimerRef.current = null;
      });
    };
  }, [fetchTemplates]);

  useEffect(() => () => {
    const request = billingRequestRef.current;
    billingRequestRef.current = null;
    request?.controller.abort();
  }, []);

  // ── Slug helpers ───────────────────────────────────────────────
  const SLUG_RE = /^[a-z0-9-]+$/;

  const toSlug = (name) =>
    name
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const handleNameBlur = () => {
    if (!centerForm.slug && centerForm.name) {
      setCenterForm(f => ({ ...f, slug: toSlug(f.name) }));
    }
  };

  const handleSlugChange = (val) => {
    setSlugError('');
    if (val && !SLUG_RE.test(val)) {
      setSlugError('Solo letras minúsculas, números y guiones');
    }
    setCenterForm(f => ({ ...f, slug: val }));
  };

  // ── isPublic toggle for calendars ──────────────────────────────
  const handleToggleIsPublic = async (svc) => {
    setSavingCalendarId(svc.id);
    try {
      await api.put(`/calendars/${svc.id}`, { ...svc, isPublic: !svc.isPublic });
      setServices(prev => prev.map(s => s.id === svc.id ? { ...s, isPublic: !s.isPublic } : s));
    } catch {
      toast.error(t('success.settings.saveError'));
    } finally {
      setSavingCalendarId(null);
    }
  };

  // ── Center settings save ───────────────────────────────────────
  const handleSaveCenter = async () => {
    setSaving(true);
    try {
      await api.put(`/centers/${center.id}`, {
        name: centerForm.name,
        timezone: centerForm.timezone,
        openingTime: centerForm.openingTime,
        closingTime: centerForm.closingTime,
        defaultSlotDuration: centerForm.defaultSlotDuration,
        freeCancelRule: centerForm.freeCancelRule,
        freeCancelCutoffDayBefore: centerForm.freeCancelCutoffDayBefore,
        freeCancelCutoffSameDay: centerForm.freeCancelCutoffSameDay,
        bookingWindowWeeks: centerForm.bookingWindowWeeks ?? 4,
        allowOverlap: centerForm.allowOverlap ?? false,
        staffPlannerVisibility: centerForm.staffPlannerVisibility ?? 'ALL',
        clientServiceVisibility: centerForm.clientServiceVisibility ?? 'ALL',
        slug: centerForm.slug || null,
        rgpdLegalText: centerForm.rgpdLegalText,
      });
      setCenter(centerForm);
      toast.success(t('common.save'));
    } catch {
      toast.error(t('settings.toast.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // ── Template dialog helpers ────────────────────────────────────
  const openCreateDialog = () => {
    setEditingTemplate(null);
    setTemplateForm(emptyTemplate());
    setActiveDay('mon');
    setClientSearch({});
    setShowTemplateDialog(true);
  };

  const openEditDialog = (tpl) => {
    setEditingTemplate(tpl);
    setTemplateForm({ name: tpl.name, days: normalizeDays(tpl.days) });
    setActiveDay('mon');
    setClientSearch({});
    setShowTemplateDialog(true);
  };

  const updateDay = (dayKey, updater) => {
    setTemplateForm(f => ({
      ...f,
      days: { ...f.days, [dayKey]: updater(f.days[dayKey]) },
    }));
  };

  // Slots
  const addSlot = (dayKey) => {
    updateDay(dayKey, d => ({
      ...d,
      slots: [...d.slots, { calendarId: '', time: '', slotsCount: 2, clientIds: [], staffId: '' }],
    }));
  };

  const removeSlot = (dayKey, idx) => {
    updateDay(dayKey, d => ({ ...d, slots: d.slots.filter((_, i) => i !== idx) }));
    setClientSearch(prev => {
      const next = { ...prev };
      delete next[`${dayKey}-${idx}`];
      return next;
    });
  };

  const updateSlot = (dayKey, idx, field, value) => {
    updateDay(dayKey, d => ({
      ...d,
      slots: d.slots.map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }));
  };

  const toggleClientInSlot = (dayKey, idx, clientId) => {
    updateDay(dayKey, d => ({
      ...d,
      slots: d.slots.map((s, i) => {
        if (i !== idx) return s;
        const has = s.clientIds.includes(clientId);
        return {
          ...s,
          clientIds: has
            ? s.clientIds.filter(id => id !== clientId)
            : [...s.clientIds, clientId],
        };
      }),
    }));
  };

  // Breaks
  const addBreak = (dayKey) => {
    updateDay(dayKey, d => ({
      ...d,
      // template-service-break-semantics (R4): default targeting is the
      // legacy whole-slot (calendarId=null, variantId=null). The dropdowns
      // in the editor surface these as "Global (slot entero)" /
      // "Todas las variantes".
      breaks: [...d.breaks, { startTime: '', endTime: '', calendarId: null, variantId: null }],
    }));
  };

  const removeBreak = (dayKey, idx) => {
    updateDay(dayKey, d => ({ ...d, breaks: d.breaks.filter((_, i) => i !== idx) }));
  };

  const updateBreak = (dayKey, idx, field, value) => {
    updateDay(dayKey, d => ({
      ...d,
      // template-service-break-semantics (R4, R4.5):
      //   - switching the service clears the variant (Planner.js:2659).
      //   - when the selected service has no active variants (dropdown
      //     `disabled`), the reducer forces variantId to null even if the
      //     row carries a stale value from a previous GET round-trip.
      breaks: d.breaks.map((b, i) => {
        if (i !== idx) return b;
        if (field === 'calendarId') {
          return { ...b, calendarId: value, variantId: null };
        }
        const activeBreakService = services.find(s => s.id === b.calendarId);
        const activeVariants = ((activeBreakService?.variants) || []).filter(v => v.active !== false);
        if (field === 'variantId' && activeVariants.length === 0) {
          return { ...b, variantId: null };
        }
        return { ...b, [field]: value };
      }),
    }));
  };

  // Save template
  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error(t('settings.toast.templateNameRequired'));
      return;
    }
    // template-service-break-semantics (Hallazgo 6, §9.3.4): guardia
    // client-side que rechaza guardar una plantilla cuando CUALQUIER break
    // de CUALQUIER día tiene `startTime` o `endTime` vacío. El input nativo
    // `<input type="time">` con `value=""` renderiza dígitos visuales
    // (00:00, 12:00, ...) y el operador confunde ese render con un valor
    // guardado, generando PUTs con `startTime: ""` y `endTime: ""`. Esta
    // guardia evita ese caso sin alterar ninguna regla de negocio existente
    // (orden, duración, solapamiento) y se ejecuta DESPUÉS del check de
    // `templateForm.name.trim()` para preservar el comportamiento previo.
    for (const [, day] of Object.entries(templateForm.days)) {
      const breaks = day.breaks || [];
      const anyEmpty = breaks.some((b) => !b.startTime || !b.endTime);
      if (anyEmpty) {
        toast.error(t('settings.toast.breakTimeRequired'));
        return;
      }
    }
    // template-service-break-semantics (R4.5): defensive normalization —
    // for any break whose service has no active variants the payload must
    // carry `variantId: null` even if the in-memory row carries a stale
    // value from a previous GET round-trip (e.g. the variant was deleted
    // from the catalog after the template was last saved).
    const normalizedDays = {};
    for (const [dayKey, day] of Object.entries(templateForm.days)) {
      const normalizedBreaks = (day.breaks || []).map((b) => {
        const activeBreakService = services.find(s => s.id === b.calendarId);
        const activeVariants = ((activeBreakService?.variants) || []).filter(v => v.active !== false);
        if (activeVariants.length === 0) {
          return { ...b, variantId: null };
        }
        return b;
      });
      normalizedDays[dayKey] = { ...day, breaks: normalizedBreaks };
    }
    const payload = { ...templateForm, days: normalizedDays };
    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        await api.put(`/weekly-templates/${editingTemplate.id}`, payload);
      } else {
        await api.post('/weekly-templates', payload);
      }
      toast.success(t('settings.toast.templateSaved'));
      setShowTemplateDialog(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('settings.toast.saveTemplateError'));
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (tpl) => {
    if (!window.confirm(t('settings.toast.confirmDeleteTemplate', { name: tpl.name }))) return;
    try {
      await api.delete(`/weekly-templates/${tpl.id}`);
      toast.success(t('settings.toast.templateDeleted'));
      fetchTemplates();
    } catch {
      toast.error(t('settings.toast.deleteTemplateError'));
    }
  };

  // ── Render helpers ─────────────────────────────────────────────

  const renderDayContent = (dayKey) => {
    const day = templateForm.days[dayKey];

    return (
      <div className="space-y-5 pt-2">
        {/* Descansos */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
              {t('settings.weeklySchedule.breaks')}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-zinc-400 hover:text-zinc-100"
              onClick={() => addBreak(dayKey)}
            >
              <Plus className="w-3 h-3 mr-1" />
              {t('settings.weeklySchedule.add')}
            </Button>
          </div>
          {day.breaks.length === 0 && (
            <p className="text-xs text-zinc-600 italic">{t('settings.weeklySchedule.noBreaks')}</p>
          )}
          {day.breaks.map((br, idx) => {
            // template-service-break-semantics (R4.2, R4.5):
            //   - the variant dropdown is rendered unconditionally when the
            //     selected service has `enablePlanningVariants === true`
            //     (no `>= 2` gate — the dropdown is meaningful even with
            //     a single active variant).
            //   - when `activeVariants.length === 0` the dropdown is rendered
            //     but `disabled`; `updateBreak` and `handleSaveTemplate`
            //     force `variantId: null` in that state so the saved payload
            //     never carries a stale variant id from a previous GET.
            const activeBreakService = services.find(s => s.id === br.calendarId);
            const featureOn = activeBreakService?.enablePlanningVariants === true;
            const activeVariants = ((activeBreakService?.variants) || []).filter(v => v.active !== false);
            const renderVariantSelect = featureOn;
            const variantSelectDisabled = activeVariants.length === 0;
            return (
              <div key={idx} className="flex items-end gap-2 mb-2 flex-wrap">
                <div className="space-y-1">
                  <Label>Servicio</Label>
                  <Select
                    value={br.calendarId || '__placeholder__'}
                    onValueChange={v => updateBreak(dayKey, idx, 'calendarId', v === '__placeholder__' ? null : v)}
                  >
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid={`template-break-service-${idx}`}>
                      <SelectValue placeholder={t('settings.weeklySchedule.breakGlobal')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__placeholder__">
                        {t('settings.weeklySchedule.breakGlobal')}
                      </SelectItem>
                      {services.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          <div className="flex items-center gap-1.5">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: s.color }}
                            />
                            {s.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {renderVariantSelect && (
                  <div className="space-y-1">
                    <Label>Variante</Label>
                    <Select
                      value={br.variantId || '__placeholder__'}
                      onValueChange={v => updateBreak(dayKey, idx, 'variantId', v === '__placeholder__' ? null : v)}
                      disabled={variantSelectDisabled}
                    >
                      <SelectTrigger className="w-44 h-8 text-xs" data-testid={`template-break-variant-${idx}`}>
                        <SelectValue placeholder={t('settings.weeklySchedule.breakAllVariants')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__placeholder__">
                          {t('settings.weeklySchedule.breakAllVariants')}
                        </SelectItem>
                        {activeVariants.map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1">
                  <Label>{t('settings.weeklySchedule.breakFrom')}</Label>
                  <Input
                    type="time"
                    value={br.startTime}
                    onChange={e => updateBreak(dayKey, idx, 'startTime', e.target.value)}
                    className={`input-tactical w-28 text-sm ${br.startTime === '' ? 'border-dashed border-amber-500/60' : ''}`}
                    data-testid={`template-break-start-${idx}`}
                  />
                </div>
                <span className="text-zinc-500 text-xs">–</span>
                <div className="space-y-1">
                  <Label>{t('settings.weeklySchedule.breakTo')}</Label>
                  <Input
                    type="time"
                    value={br.endTime}
                    onChange={e => updateBreak(dayKey, idx, 'endTime', e.target.value)}
                    className={`input-tactical w-28 text-sm ${br.endTime === '' ? 'border-dashed border-amber-500/60' : ''}`}
                    data-testid={`template-break-end-${idx}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeBreak(dayKey, idx)}
                  className="text-zinc-600 hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Slots */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
              {t('settings.weeklySchedule.slots')}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-zinc-400 hover:text-zinc-100"
              onClick={() => addSlot(dayKey)}
            >
              <Plus className="w-3 h-3 mr-1" />
              {t('settings.weeklySchedule.add')}
            </Button>
          </div>
          {day.slots.length === 0 && (
            <p className="text-xs text-zinc-600 italic">{t('settings.weeklySchedule.noSlots')}</p>
          )}
          {day.slots.map((slot, idx) => {
            const searchKey = `${dayKey}-${idx}`;
            const query = (clientSearch[searchKey] || '').toLowerCase();
            const filtered = clients.filter(c =>
              !query || (c.fullName || '').toLowerCase().includes(query)
            );
            return (
              <div
                key={idx}
                className="border border-zinc-800 rounded-sm p-3 mb-3 space-y-2"
              >
                {/* Row 1: service + time + duration + remove */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Select
                    value={slot.calendarId}
                    onValueChange={v => updateSlot(dayKey, idx, 'calendarId', v)}
                  >
                    <SelectTrigger className="w-36 h-8 text-xs">
                      <SelectValue placeholder={t('settings.weeklySchedule.slots')} />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          <div className="flex items-center gap-1.5">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: s.color }}
                            />
                            {s.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="time"
                    value={slot.time}
                    onChange={e => updateSlot(dayKey, idx, 'time', e.target.value)}
                    className="input-tactical w-28 h-8 text-xs"
                  />
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={1}
                      max={8}
                      value={slot.slotsCount}
                      onChange={e => updateSlot(dayKey, idx, 'slotsCount', Number(e.target.value))}
                      className="input-tactical w-14 h-8 text-xs text-center"
                    />
                    <span className="text-zinc-500 text-xs whitespace-nowrap">
                      slots ({slot.slotsCount * 15}min)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSlot(dayKey, idx)}
                    className="ml-auto text-zinc-600 hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Row 2: client selector */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-zinc-500 uppercase tracking-wide">
                      {t('settings.weeklySchedule.clients')} ({slot.clientIds.length})
                    </span>
                  </div>
                  {/* Selected badges */}
                  {slot.clientIds.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {slot.clientIds.map(cid => {
                        const c = clients.find(x => x.id === cid);
                        return c ? (
                          <span
                            key={cid}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-200 text-[11px]"
                          >
                            {c.fullName}
                            <button
                              type="button"
                              onClick={() => toggleClientInSlot(dayKey, idx, cid)}
                              className="text-zinc-500 hover:text-zinc-200"
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                  {/* Search + list */}
                  <Input
                    value={clientSearch[searchKey] || ''}
                    onChange={e =>
                      setClientSearch(prev => ({ ...prev, [searchKey]: e.target.value }))
                    }
                    placeholder={t('settings.weeklySchedule.searchClient')}
                    className="input-tactical h-7 text-xs mb-1"
                  />
                  <div className="max-h-28 overflow-y-auto border border-zinc-800 rounded-sm">
                    {filtered.length === 0 ? (
                      <p className="px-2 py-1.5 text-xs text-zinc-600">Sin resultados</p>
                    ) : (
                      filtered.map(c => {
                        const checked = slot.clientIds.includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => toggleClientInSlot(dayKey, idx, c.id)}
                            className={`w-full text-left px-2 py-1 text-xs flex items-center gap-2 hover:bg-zinc-800 transition-colors ${
                              checked ? 'bg-zinc-800/60 text-zinc-100' : 'text-zinc-400'
                            }`}
                          >
                            <span
                              className={`w-3 h-3 border rounded-sm flex-shrink-0 flex items-center justify-center ${
                                checked ? 'bg-zinc-100 border-zinc-100' : 'border-zinc-600'
                              }`}
                            >
                              {checked && (
                                <svg viewBox="0 0 10 10" className="w-2 h-2 text-zinc-900">
                                  <path d="M1 5l3 3 5-5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                </svg>
                              )}
                            </span>
                            {c.fullName}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Row 3: staff selector */}
                {slot.calendarId && staffUsers.filter(s => (s.serviceIds || []).includes(slot.calendarId)).length > 0 && (
                  <div>
                    <span className="text-[11px] text-zinc-500 uppercase tracking-wide block mb-1">
                      Personal asignado
                    </span>
                    <Select
                      value={slot.staffId || 'auto'}
                      onValueChange={v => updateSlot(dayKey, idx, 'staffId', v === 'auto' ? '' : v)}
                    >
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Asignar automáticamente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Asignar automáticamente</SelectItem>
                        {staffUsers
                          .filter(s => (s.serviceIds || []).includes(slot.calendarId))
                          .map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.fullName}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <SettingsIcon className="w-6 h-6 text-zinc-400" />
        <h1 className="font-display text-2xl font-bold tracking-tight uppercase">
          {t('settings.title')}
        </h1>
      </div>

      <Tabs defaultValue="general" className="max-w-2xl">
        <TabsList className="mb-6 bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="general" className="data-[state=active]:bg-zinc-800">
            <Globe className="w-4 h-4 mr-2" />
            {t('settings.tabs.general')}
          </TabsTrigger>
          {(isCenterAdmin || isSuperAdmin) && (
            <TabsTrigger value="centro" className="data-[state=active]:bg-zinc-800">
              <Building2 className="w-4 h-4 mr-2" />
              {t('settings.tabs.center')}
            </TabsTrigger>
          )}
          {isCenterAdmin && (
            <TabsTrigger value="horario" className="data-[state=active]:bg-zinc-800">
              <CalendarDays className="w-4 h-4 mr-2" />
              {t('settings.tabs.schedule')}
            </TabsTrigger>
          )}
          {isCenterAdmin && center?.aiSecretaryEnabled && (
            <TabsTrigger value="ai-secretary" className="data-[state=active]:bg-zinc-800">
              <Bot className="w-4 h-4 mr-2" />
              Secretaria IA
            </TabsTrigger>
          )}
          {isCenterAdmin && center?.whatsappStatus === 'active' && (
            <TabsTrigger value="whatsapp" className="data-[state=active]:bg-zinc-800">
              <MessageSquare className="w-4 h-4 mr-2" />
              WhatsApp
            </TabsTrigger>
          )}
          {isCenterAdmin && (
            <TabsTrigger value="facturacion" className="data-[state=active]:bg-zinc-800">
              <CreditCard className="w-4 h-4 mr-2" />
              Facturación
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── Tab: General ────────────────────────────────── */}
        <TabsContent value="general">
          <div className="p-4 bg-card border border-zinc-800 rounded-sm">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-zinc-400" />
              <h2 className="font-display font-semibold uppercase tracking-wide">
                {t('settings.language')}
              </h2>
            </div>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-48" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="de">Deutsch</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </TabsContent>

        {/* ── Tab: Centro ─────────────────────────────────── */}
        <TabsContent value="centro">
          {(isCenterAdmin || isSuperAdmin) && center && (
            <div className="p-4 bg-card border border-zinc-800 rounded-sm">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-zinc-400" />
                <h2 className="font-display font-semibold uppercase tracking-wide">
                  {t('settings.centerSettings')}
                </h2>
              </div>

              {loadingCenter ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('centers.name')}</Label>
                    <Input
                      value={centerForm.name || ''}
                      onChange={e => setCenterForm(f => ({ ...f, name: e.target.value }))}
                      onBlur={handleNameBlur}
                      className="input-tactical"
                      data-testid="settings-center-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('centers.timezone')}</Label>
                    <Input
                      value={centerForm.timezone || ''}
                      onChange={e => setCenterForm(f => ({ ...f, timezone: e.target.value }))}
                      className="input-tactical"
                      data-testid="settings-timezone"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t('centers.openingTime')}</Label>
                      <Input
                        type="time"
                        value={centerForm.openingTime || ''}
                        onChange={e => setCenterForm(f => ({ ...f, openingTime: e.target.value }))}
                        className="input-tactical"
                        data-testid="settings-opening"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('centers.closingTime')}</Label>
                      <Input
                        type="time"
                        value={centerForm.closingTime || ''}
                        onChange={e => setCenterForm(f => ({ ...f, closingTime: e.target.value }))}
                        className="input-tactical"
                        data-testid="settings-closing"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('centers.slotDuration')} (min)</Label>
                    <Input
                      type="number"
                      value={centerForm.defaultSlotDuration || 15}
                      onChange={e => setCenterForm(f => ({ ...f, defaultSlotDuration: Number(e.target.value) }))}
                      className="input-tactical w-24"
                      min={5}
                      step={5}
                      data-testid="settings-slot-duration"
                    />
                  </div>

                  <div className="pt-2 border-t border-zinc-800">
                    <h3 className="font-display font-semibold uppercase tracking-wide mb-3">
                      Privacidad y normas del centro
                    </h3>
                    <div className="space-y-2">
                      <Label htmlFor="settings-rgpd-legal-text">Texto de privacidad y normas</Label>
                      <Textarea
                        id="settings-rgpd-legal-text"
                        value={centerForm.rgpdLegalText || ''}
                        onChange={e => setCenterForm(f => ({ ...f, rgpdLegalText: e.target.value }))}
                        className="input-tactical min-h-28"
                        data-testid="settings-rgpd-legal-text"
                      />
                      <p className="text-xs text-zinc-500">
                        Texto informativo que verán los clientes sobre privacidad, tratamiento de datos y condiciones particulares del centro.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Semanas de antelación máxima para reservas</Label>
                    <Input
                      type="number"
                      value={centerForm.bookingWindowWeeks ?? 4}
                      onChange={e => setCenterForm(f => ({ ...f, bookingWindowWeeks: Number(e.target.value) }))}
                      className="input-tactical w-24"
                      min={1}
                      max={52}
                      data-testid="settings-booking-window-weeks"
                    />
                    <p className="text-xs text-zinc-500">La IA y el área de cliente no podrán reservar más allá de este plazo</p>
                  </div>

                  <div className="pt-2 border-t border-zinc-800">
                    <h3 className="font-display font-semibold uppercase tracking-wide mb-3">
                      {t('settings.cancelRules.title')}
                    </h3>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>{t('settings.cancelRules.rule')}</Label>
                        <Select
                          value={centerForm.freeCancelRule || 'DAY_BEFORE'}
                          onValueChange={v => setCenterForm(f => ({ ...f, freeCancelRule: v }))}
                        >
                          <SelectTrigger className="w-64">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="DAY_BEFORE">{t('settings.cancelRules.dayBefore')}</SelectItem>
                            <SelectItem value="SAME_DAY">{t('settings.cancelRules.sameDay')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>{t('settings.cancelRules.cutoffDayBefore')}</Label>
                          <Input
                            type="time"
                            value={centerForm.freeCancelCutoffDayBefore || '21:30'}
                            onChange={e => setCenterForm(f => ({ ...f, freeCancelCutoffDayBefore: e.target.value }))}
                            className="input-tactical"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('settings.cancelRules.cutoffSameDay')}</Label>
                          <Input
                            type="time"
                            value={centerForm.freeCancelCutoffSameDay || '09:00'}
                            onChange={e => setCenterForm(f => ({ ...f, freeCancelCutoffSameDay: e.target.value }))}
                            className="input-tactical"
                          />
                        </div>
                      </div>

                      <p className="text-xs text-zinc-500">
                        {t('settings.cancelRules.note')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-4 p-3 bg-zinc-900 border border-zinc-800 rounded-sm">
                    <div>
                      <p className="text-sm font-medium text-zinc-200">Permitir citas solapadas</p>
                      <p className="text-xs text-zinc-500 mt-0.5">Las citas pueden coincidir en el mismo horario.</p>
                    </div>
                    <Switch
                      checked={centerForm.allowOverlap ?? false}
                      onCheckedChange={v => setCenterForm(f => ({ ...f, allowOverlap: v }))}
                    />
                  </div>

                  <div className="flex items-start justify-between gap-4 p-3 bg-zinc-900 border border-zinc-800 rounded-sm">
                    <div>
                      <p className="text-sm font-medium text-zinc-200">El personal solo ve sus propias reservas en el Planner</p>
                      <p className="text-xs text-zinc-500 mt-0.5">Activa esta opción para que el personal solo gestione sus propias citas.</p>
                    </div>
                    <Switch
                      checked={centerForm.staffPlannerVisibility === 'OWN_ONLY'}
                      onCheckedChange={v => setCenterForm(f => ({ ...f, staffPlannerVisibility: v ? 'OWN_ONLY' : 'ALL' }))}
                    />
                  </div>

                  <div className="flex items-start justify-between gap-4 p-3 bg-zinc-900 border border-zinc-800 rounded-sm">
                    <div>
                      <p className="text-sm font-medium text-zinc-200">Mostrar solo servicios contratados</p>
                      <p className="text-xs text-zinc-500 mt-0.5">Los clientes solo verán en el planificador los servicios incluidos en un bono o cuota vigente.</p>
                    </div>
                    <Switch
                      checked={(centerForm.clientServiceVisibility ?? 'ALL') === 'ENTITLED_ONLY'}
                      onCheckedChange={v => setCenterForm(f => ({ ...f, clientServiceVisibility: v ? 'ENTITLED_ONLY' : 'ALL' }))}
                      data-testid="client-service-visibility-switch"
                    />
                  </div>

                  {/* Slug — URL de reservas públicas */}
                  <div className="pt-2 border-t border-zinc-800">
                    <h3 className="font-display font-semibold uppercase tracking-wide mb-3 text-sm">
                      Reservas online
                    </h3>
                    <div className="space-y-2">
                      <Label>URL de reservas públicas</Label>
                      <div className="flex items-center gap-0">
                        <span className="px-3 py-2 bg-zinc-800 border border-r-0 border-zinc-700 rounded-l-sm text-xs text-zinc-400 whitespace-nowrap">
                          app.adamsched.com/reserva/
                        </span>
                        <Input
                          value={centerForm.slug || ''}
                          onChange={e => handleSlugChange(e.target.value)}
                          onBlur={handleNameBlur}
                          className="input-tactical rounded-l-none"
                          placeholder="mi-centro"
                        />
                      </div>
                      {slugError && (
                        <p className="text-xs text-red-400">{slugError}</p>
                      )}
                      <p className="text-xs text-zinc-500">
                        Comparte esta URL en Google Business Profile, redes sociales o tu web.
                      </p>
                    </div>
                  </div>

                  {/* isPublic toggle por servicio */}
                  {services.length > 0 && (
                    <div className="space-y-2">
                      <Label>Servicios visibles en reservas online</Label>
                      <div className="space-y-2">
                        {services.map(svc => (
                          <div
                            key={svc.id}
                            className="flex items-center justify-between px-3 py-2 border border-zinc-800 rounded-sm"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: svc.color || '#888' }}
                              />
                              <span className="text-sm text-zinc-300 truncate">{svc.name}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {savingCalendarId === svc.id && (
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />
                              )}
                              <Switch
                                checked={svc.isPublic ?? false}
                                onCheckedChange={() => handleToggleIsPublic(svc)}
                                disabled={savingCalendarId === svc.id}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      {centerForm.slug && (
                        <p className="text-xs text-zinc-500">
                          URL del widget: <span className="text-zinc-300">app.adamsched.com/reserva/{centerForm.slug}</span>
                        </p>
                      )}
                    </div>
                  )}

                  <CenterFaqsSection
                    faqs={centerForm.faqs || []}
                    status={faqStatus}
                    error={faqError}
                    draft={faqDraft}
                    editingIndex={editingFaqIndex}
                    onDraftChange={setFaqDraft}
                    onSaveDraft={handleFaqSave}
                    onDelete={handleFaqDelete}
                    onEdit={handleFaqEdit}
                    onCancelEdit={() => { setEditingFaqIndex(null); setFaqDraft({ question: '', answer: '' }); }}
                    onRetry={fetchCenter}
                    onSaveFaqs={handleSaveFaqs}
                    saving={savingFaqs}
                    saveError={faqSaveError}
                    saveSuccess={faqSaveSuccess}
                  />

                  <Button
                    className="btn-primary"
                    onClick={handleSaveCenter}
                    disabled={saving}
                    data-testid="save-settings-btn"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {t('common.save')}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ── Tab: Horario Semanal ─────────────────────────── */}
        <TabsContent value="horario">
          <div className="p-4 bg-card border border-zinc-800 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-zinc-400" />
                <h2 className="font-display font-semibold uppercase tracking-wide">
                  {t('settings.weeklySchedule.title')}
                </h2>
              </div>
              <Button
                size="sm"
                className="btn-primary"
                onClick={openCreateDialog}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('settings.weeklySchedule.newTemplate')}
              </Button>
            </div>

            {loadingTemplates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
              </div>
            ) : templatesError ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="weekly-templates-error" role="alert" aria-live="polite">
                <p className="text-sm text-red-300">{templatesError}</p>
                <Button type="button" variant="outline" size="sm" data-testid="weekly-templates-retry" onClick={fetchTemplates}>
                  Reintentar
                </Button>
              </div>
            ) : templates.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-6">{t('settings.weeklySchedule.noTemplates')}</p>
            ) : (
              <div className="space-y-2">
                {templates.map(tpl => (
                  <div
                    key={tpl.id}
                    className="flex items-center justify-between px-3 py-2.5 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors"
                  >
                    <span className="text-sm font-medium">{tpl.name}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-zinc-400 hover:text-zinc-100"
                        onClick={() => openEditDialog(tpl)}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-zinc-400 hover:text-red-400"
                        onClick={() => handleDeleteTemplate(tpl)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Tab: Secretaria IA ───────────────────────────────── */}
        {isCenterAdmin && center?.whatsappStatus === 'active' && (
          <TabsContent value="whatsapp">
            <div className="p-4 bg-card border border-zinc-800 rounded-sm">
              <div className="flex items-center gap-2 mb-5">
                <MessageSquare className="w-5 h-5 text-zinc-400" />
                <h2 className="font-display font-semibold uppercase tracking-wide">WhatsApp</h2>
              </div>
              <WhatsAppDashboard />
            </div>
          </TabsContent>
        )}

        {/* ── Tab: Facturación ────────────────────────────────── */}
        {isCenterAdmin && (
          <TabsContent value="facturacion">
            <div className="p-4 bg-card border border-zinc-800 rounded-sm">
              <div className="flex items-center gap-2 mb-5">
                <CreditCard className="w-5 h-5 text-zinc-400" />
                <h2 className="font-display font-semibold uppercase tracking-wide">Facturación</h2>
              </div>
              <p className="text-sm text-zinc-400 mb-4">
                Genera un enlace seguro y de un solo uso para actualizar la tarjeta de pago de tu centro.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={handleUpdatePaymentMethod}
                disabled={loadingPaymentLink}
              >
                {loadingPaymentLink
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <CreditCard className="w-3.5 h-3.5" />
                }
                Actualizar método de pago
              </Button>
              {billingPaymentLinkUnknown && (
                <div className="mt-3 space-y-2" data-testid="billing-payment-link-unknown" role="alert" aria-live="polite">
                  <p className="text-sm text-amber-300">No se pudo confirmar el enlace. Puedes reintentarlo; se generará otro enlace y se invalidará el anterior.</p>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="billing-payment-link-close"
                      onClick={() => {
                        setBillingPaymentLinkUnknown(false);
                        setLoadingPaymentLink(false);
                      }}
                    >
                      Cerrar
                    </Button>
                    <Button type="button" variant="outline" size="sm" data-testid="billing-payment-link-retry" onClick={handleUpdatePaymentMethod}>
                      Reintentar
                    </Button>
                  </div>
                </div>
              )}
              <div className="mt-6 pt-5 border-t border-zinc-800">
                <h3 className="font-display font-semibold uppercase tracking-wide mb-2">{t('settings.connect.title')}</h3>
                <p className="text-sm text-zinc-400 mb-4">{t('settings.connect.description')}</p>
                {connectStatus && (
                  <p className="text-sm text-zinc-300 mb-3">
                    {t('settings.connect.status')}: <span className="font-medium">{connectStatus.status}</span>
                  </p>
                )}
                <Button size="sm" variant="outline" onClick={handleConnectAction} disabled={loadingConnect} className="gap-1.5">
                  {loadingConnect && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {!loadingConnect && (connectStatus?.status === 'connected' ? t('settings.connect.requirements') : t('settings.connect.action'))}
                </Button>
              </div>
            </div>
          </TabsContent>
        )}

        {isCenterAdmin && center?.aiSecretaryEnabled && (
          <TabsContent value="ai-secretary">
            <div className="p-4 bg-card border border-zinc-800 rounded-sm space-y-6">
              <div className="flex items-center gap-2 flex-wrap">
                <Bot className="w-5 h-5 text-zinc-400" />
                <h2 className="font-display font-semibold uppercase tracking-wide">Secretaria IA</h2>
                {aiSecConfig && (
                  <>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                      aiSecConfig.enabled
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-orange-500/10 text-orange-400'
                    }`}>
                      {aiSecConfig.enabled ? 'Activa' : 'En pausa'}
                    </span>
                    {!aiSecConfig.voiceLimitReached && (
                      <Button
                        size="sm"
                        variant="outline"
                        className={`ml-auto gap-1.5 text-xs h-7 ${
                          aiSecConfig.enabled
                            ? 'border-orange-500/40 text-orange-400 hover:bg-orange-500/10'
                            : 'border-green-500/40 text-green-400 hover:bg-green-500/10'
                        }`}
                        onClick={handleTogglePause}
                        disabled={togglingPause}
                      >
                        {togglingPause
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : aiSecConfig.enabled
                            ? <PauseCircle className="w-3.5 h-3.5" />
                            : <PlayCircle className="w-3.5 h-3.5" />
                        }
                        {aiSecConfig.enabled ? 'Pausar' : 'Reactivar'}
                      </Button>
                    )}
                  </>
                )}
              </div>

              {/* Toggle ampliaciones automáticas */}
              {aiSecConfig && (
                <div className="flex items-start justify-between gap-4 p-3 bg-zinc-900 border border-zinc-800 rounded-sm">
                  <div>
                    <p className="text-sm font-medium text-zinc-200">Ampliaciones automáticas de minutos</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {(aiSecConfig.autoAddonEnabled ?? true)
                        ? 'Si agota los minutos incluidos, se añadirán 200 minutos adicionales automáticamente (39€ primera vez, 34€ siguientes)'
                        : 'Si agota los minutos incluidos, el asistente se pausará automáticamente hasta el siguiente ciclo de facturación'}
                    </p>
                  </div>
                  <Switch
                    checked={aiSecConfig.autoAddonEnabled ?? true}
                    onCheckedChange={handleToggleAutoAddon}
                  />
                </div>
              )}

              {/* Límite alcanzado banner */}
              {aiSecConfig?.voiceLimitReached && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm text-sm text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  Has alcanzado el límite de minutos. Contacta con AdamSched para ampliar tu plan.
                </div>
              )}

              {/* Barra de minutos */}
              {aiSecConfig && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>Minutos usados este mes</span>
                    <span>
                      {(aiSecConfig.voiceMinutesUsed || 0).toFixed(1)} / {aiSecConfig.voiceMinutesIncluded || 60} min
                    </span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        aiSecConfig.voiceLimitReached ? 'bg-red-500' : 'bg-green-500'
                      }`}
                      style={{
                        width: `${Math.min(100, ((aiSecConfig.voiceMinutesUsed || 0) / (aiSecConfig.voiceMinutesIncluded || 60)) * 100)}%`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-zinc-500">
                    <span>
                      Restantes: {Math.max(0, (aiSecConfig.voiceMinutesIncluded || 60) - (aiSecConfig.voiceMinutesUsed || 0)).toFixed(1)} min
                    </span>
                    <span>
                      {Math.min(100, Math.round(((aiSecConfig.voiceMinutesUsed || 0) / (aiSecConfig.voiceMinutesIncluded || 60)) * 100))}% consumido
                    </span>
                  </div>
                  {aiSecConfig.voiceLimitReached && (
                    <p className="text-xs text-red-400">Límite de minutos alcanzado. La secretaria IA está desactivada hasta el próximo ciclo.</p>
                  )}
                </div>
              )}

              {/* Nombre del centro para la IA */}
              <div className="space-y-2">
                <Label>Nombre del centro para la IA</Label>
                <Input
                  value={aiSecForm.businessName}
                  onChange={e => setAiSecForm(f => ({ ...f, businessName: e.target.value }))}
                  placeholder="Ej: Bodyquick, Centro Wellness..."
                  className="input-tactical"
                />
                <p className="text-xs text-zinc-500">
                  Este nombre se inyecta en el prompt del asistente. Si se deja vacío se usa el nombre del centro.
                </p>
              </div>

              {/* Información del negocio */}
              <div className="space-y-2">
                <Label>Información del negocio</Label>
                <Textarea
                  rows={4}
                  value={aiSecForm.businessInfo}
                  onChange={e => setAiSecForm(f => ({ ...f, businessInfo: e.target.value }))}
                  placeholder="Descripción del centro, servicios principales, horarios..."
                  className="input-tactical resize-none"
                />
              </div>

              {/* Mensaje de bienvenida */}
              <div className="space-y-2">
                <Label>Mensaje de bienvenida</Label>
                <Input
                  value={aiSecForm.welcomeMessage}
                  onChange={e => setAiSecForm(f => ({ ...f, welcomeMessage: e.target.value }))}
                  placeholder="Hola, soy la secretaria virtual de..."
                  className="input-tactical"
                />
              </div>

              {/* Mensaje de derivación */}
              <div className="space-y-2">
                <Label>Mensaje de derivación</Label>
                <Input
                  value={aiSecForm.fallbackMessage}
                  onChange={e => setAiSecForm(f => ({ ...f, fallbackMessage: e.target.value }))}
                  placeholder="Lo siento, voy a notificar al equipo para que te llamen..."
                  className="input-tactical"
                />
              </div>

              {/* Política de cancelación */}
              <div className="space-y-2">
                <Label>Política de cancelación</Label>
                <Textarea
                  rows={3}
                  value={aiSecForm.cancellationPolicyText}
                  onChange={e => setAiSecForm(f => ({ ...f, cancellationPolicyText: e.target.value }))}
                  placeholder="Las cancelaciones deben realizarse con al menos 24h de antelación..."
                  className="input-tactical resize-none"
                />
              </div>

              {/* Toggle derivación humana */}
              <div className="flex items-center justify-between">
                <div>
                  <Label>Derivación a persona humana</Label>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Permite que la secretaria notifique al equipo cuando no pueda resolver una consulta
                  </p>
                </div>
                <Switch
                  checked={aiSecForm.humanHandoffEnabled}
                  onCheckedChange={v => setAiSecForm(f => ({ ...f, humanHandoffEnabled: v }))}
                />
              </div>

              {/* Guardar */}
              <div className="flex justify-end pt-2 border-t border-zinc-800">
                <Button className="btn-primary" onClick={handleSaveAiSec} disabled={savingAiSec}>
                  {savingAiSec ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar configuración
                </Button>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* ── Template Editor Dialog ──────────────────────────── */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-display uppercase tracking-wide">
              {editingTemplate ? t('settings.weeklySchedule.editTemplate') : t('settings.weeklySchedule.newTemplate')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('settings.weeklySchedule.editorDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* Name */}
            <div className="space-y-1.5">
              <Label>{t('settings.weeklySchedule.nameLabel')}</Label>
              <Input
                value={templateForm.name}
                onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))}
                placeholder={t('settings.weeklySchedule.namePlaceholder')}
                className="input-tactical"
              />
            </div>

            {/* Day selector */}
            <div className="flex gap-1">
              {DAY_KEYS.map(k => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setActiveDay(k)}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                    activeDay === k
                      ? 'bg-zinc-100 text-zinc-900'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'
                  }`}
                >
                  {t(`settings.days.${k}`)}
                </button>
              ))}
            </div>

            {/* Day content */}
            <div className="border border-zinc-800 rounded-sm p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-3">
                {t(`settings.days.${activeDay}`)}
              </p>
              {renderDayContent(activeDay)}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 flex-shrink-0 pt-4 border-t border-zinc-800">
            <Button
              variant="outline"
              onClick={() => setShowTemplateDialog(false)}
              disabled={savingTemplate}
            >
              {t('common.cancel')}
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSaveTemplate}
              disabled={savingTemplate}
            >
              {savingTemplate ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('common.save')}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
